import { ReactNode } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  List,
  ListProps,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import type { LayerSection } from 'features/mapping/mapping.types';

import { LayerSectionIcon } from './icons/LayerSectionIcon';
import {
  layersDrawerChevronButtonSx,
  layersDrawerListPaddingSx,
} from './layersDrawer.styles';

type LayersSectionProps = {
  children: ReactNode;
  title: string;
  section: LayerSection;
  /** MUI palette token or CSS color for the section icon. */
  iconColor?: string;
  onToggleCollapse?: () => void;
  expanded?: boolean;
  listProps?: ListProps;
};

export const LayersSection = (props: LayersSectionProps): JSX.Element => {
  const {
    children,
    title,
    section,
    iconColor = 'text.secondary',
    onToggleCollapse,
    expanded,
    listProps,
  } = props;
  const headerId = `layers-section-header-${section}`;

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <ButtonBase
        onClick={onToggleCollapse}
        aria-labelledby={headerId}
        aria-expanded={expanded}
        sx={{
          width: '100%',
          minHeight: 60,
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          justifyContent: 'flex-start',
          bgcolor: 'background.default',
          borderBottom: expanded ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <LayerSectionIcon
          section={section}
          color={iconColor}
          data-testid="layers-section-icon"
        />
        <Typography
          variant="h3"
          id={headerId}
          sx={{
            flex: 1,
            textAlign: 'left',
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
        <Box
          data-testid="layers-section-toggle"
          aria-hidden
          sx={{
            ...layersDrawerChevronButtonSx,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {expanded ? (
            <KeyboardArrowUpIcon sx={{ color: 'text.primary' }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ color: 'text.primary' }} />
          )}
        </Box>
      </ButtonBase>
      <Collapse in={expanded}>
        <List
          disablePadding
          {...listProps}
          sx={[layersDrawerListPaddingSx, listProps?.sx] as SxProps<Theme>}
        >
          {children}
        </List>
      </Collapse>
    </Box>
  );
};
